/**
 * #1150: shared-presentation model for Novig quotes and the manual ticket.
 *
 * Pure: callers pass the clock, readiness and the board-key → Novig identity
 * mapping. Nothing here subscribes, polls, resolves an account or places an
 * order; the first producer/adapter supplies these inputs through the seam in
 * `seam.ts` (contract: docs/261012NovigDiscoveryTicketPlan.md).
 *
 * Units stay native: price is Novig's three-decimal probability string,
 * quantity a whole count of $0.01-payout contracts, money exact USD text from
 * `computeNovigNativeOrderAmounts`. No cents display, no default quantity and
 * no snapping of off-grid input.
 */
import type { PriceMode, TeamSide } from '@/views/ValueDashboard/types';
import { computeNovigNativeOrderAmounts, type NovigNativeOrderAmounts } from './orderAmounts';

/** Market-data readiness, independent of account readiness. `observedAtMs`
 * is when the producer last observed the quote (displayed age only). */
export type NovigFeedReadiness =
  | { readonly state: 'fresh' | 'stale'; readonly observedAtMs: number }
  | { readonly state: 'absent' }
  | { readonly state: 'withdrawn'; readonly observedAtMs: number | null };

/** Trading readiness for the selected org. Synthetic authority is fixture
 * evidence only; it never establishes real Novig access. */
export type NovigAccountReadiness =
  | { readonly state: 'absent' }
  | { readonly state: 'read-only' | 'synthetic-authorized'; readonly subaccountId: string };

export interface NovigOutcomeQuote {
  readonly eventId: string;
  readonly marketId: string;
  readonly outcomeId: string;
  readonly label: string;
  readonly side: TeamSide;
  /** Best bid / best ask as canonical probability text, null when absent. */
  readonly bestBid: string | null;
  readonly bestAsk: string | null;
}

/** One full-game moneyline: each team row on the board shows its own outcome. */
export interface NovigBoardQuote {
  readonly eventId: string;
  readonly marketId: string;
  readonly away: NovigOutcomeQuote | null;
  readonly home: NovigOutcomeQuote | null;
  readonly feed: NovigFeedReadiness;
}

/** Keyed by `GameRow.key`. The supplier owns the identity mapping. */
export type NovigBoardQuotes = ReadonlyMap<string, NovigBoardQuote>;

export interface NovigFeedView {
  /** Short status for a fixed-height line; '' when fresh. */
  readonly status: string;
  readonly blocksPlacement: boolean;
  /** Reason shown at the action, null when the feed doesn't block. */
  readonly reason: string | null;
  /** `reason` without the ticking quote age, for live regions that must
   *  announce a state change, not every second of a stale quote. */
  readonly reasonWithoutAge: string | null;
  /** Quote age when stale ("42s"), else null. */
  readonly age: string | null;
}

export function formatNovigQuoteAge(observedAtMs: number, nowMs: number): string {
  const s = Math.max(0, Math.floor((nowMs - observedAtMs) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`;
}

export function presentNovigFeed(feed: NovigFeedReadiness, nowMs: number): NovigFeedView {
  switch (feed.state) {
    case 'fresh':
      return { status: '', blocksPlacement: false, reason: null, reasonWithoutAge: null, age: null };
    case 'stale': {
      const age = formatNovigQuoteAge(feed.observedAtMs, nowMs);
      return { status: `Stale ${age}`, blocksPlacement: true, reason: `Novig quote is stale (last seen ${age} ago)`,
        reasonWithoutAge: 'Novig quote is stale', age };
    }
    case 'withdrawn':
      return { status: 'Withdrawn', blocksPlacement: true, reason: 'Novig withdrew this market',
        reasonWithoutAge: 'Novig withdrew this market', age: null };
    case 'absent':
      return { status: 'No Novig quote', blocksPlacement: true, reason: 'No Novig market data for this game',
        reasonWithoutAge: 'No Novig market data for this game', age: null };
  }
}

/** Account reason at the action, or null when the account could place. */
export function novigAccountReason(account: NovigAccountReadiness): string | null {
  if (account.state === 'absent') return 'No Novig account for this organization';
  if (account.state === 'read-only') return 'Your Novig access is read-only';
  return null;
}

/** The price a team-row pill shows. Maker → best bid, taker → best ask; Novig
 * publishes no last-trade price here, so market mode shows none. */
export function novigPillProbability(
  quote: NovigBoardQuote, side: TeamSide, priceMode: PriceMode,
): string | null {
  const outcome = quote[side];
  if (!outcome || quote.feed.state !== 'fresh' || priceMode === 'market') return null;
  return priceMode === 'maker' ? outcome.bestBid : outcome.bestAsk;
}

/** Draft identity: switching any part retires the draft rather than retargeting it. */
export function novigDraftKey(account: NovigAccountReadiness, outcome: NovigOutcomeQuote): string {
  const sub = account.state === 'absent' ? '' : account.subaccountId;
  return JSON.stringify([sub, outcome.eventId, outcome.marketId, outcome.outcomeId]);
}

export interface NovigTicketActionView {
  /** Always true until an execution adapter exists (`liveSubmitEnabled:false`);
   *  the submit control itself says so, so `reasons` never needs to. */
  readonly submitDisabled: true;
  /** Every actionable reason that would stop placement, most fundamental first. */
  readonly reasons: readonly string[];
  /** Exact native cost/payout once both terms are valid, else null. */
  readonly amounts: NovigNativeOrderAmounts | null;
}

/** Label of the always-disabled submit until an execution adapter exists. */
export const NOVIG_SUBMIT_NOT_CONNECTED = 'Place Novig order — not connected';

function quantityReason(text: string): string | null {
  if (text === '') return 'Enter a quantity in contracts';
  return /^[1-9]\d*$/.test(text) && Number.isSafeInteger(Number(text))
    ? null : 'Quantity must be a whole number of contracts';
}

function priceReason(text: string): string | null {
  if (text === '') return 'Enter a price';
  const r = computeNovigNativeOrderAmounts(text, 1);
  if (r.ok) return null;
  return r.reason === 'off-grid-probability'
    ? `${text} is off Novig's price grid (0.005 steps from 0.055 to 0.945)`
    : 'Price must be a probability like 0.665';
}

export function presentNovigTicketAction(input: {
  feed: NovigFeedReadiness;
  account: NovigAccountReadiness;
  priceText: string;
  qtyText: string;
  nowMs: number;
}): NovigTicketActionView {
  const pReason = priceReason(input.priceText);
  const qReason = quantityReason(input.qtyText);
  const reasons = [
    novigAccountReason(input.account),
    presentNovigFeed(input.feed, input.nowMs).reason,
    pReason,
    qReason,
  ].filter((r): r is string => r !== null);
  const result = pReason === null && qReason === null
    ? computeNovigNativeOrderAmounts(input.priceText, Number(input.qtyText)) : null;
  return { submitDisabled: true, reasons, amounts: result?.ok ? result.amounts : null };
}
